// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { createBrowserRouter, Outlet, RouteObject, RouterProvider } from 'react-router-dom';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProviderCustom } from './ThemeProviderCustom';
import { JSON_API_URL, VALIDATOR_API_URL } from './config/externalApis';
import { AllocationApiProvider } from './contexts/AllocationApiProvider';
import { AuthProviders } from './contexts/AuthProviders';
import { CNAllocationApiProvider } from './contexts/CNAllocationApiProvider';
import { JsonApiProvider } from './contexts/JsonApiProvider';
import { LedgerApiClientProvider } from './contexts/LedgerApiProvider';
import { useLocalStorage } from './contexts/LocalStorageContext';
import { LocalStorageProvider } from './contexts/LocalStorageProvider';
import { ModuleProvider } from './contexts/ModuleProvider';
import { PartiesProvider } from './contexts/PartiesProvider';
import { ScanProxyApiProvider } from './contexts/ScanProxyApiProvider';
import { UtilityAutomationApiClientProvider } from './contexts/UtilityAutomationApiClientProvider';
import { UtilityOperatorProxyApiProvider } from './contexts/UtilityOperatorApiProvider';
import { useConfig } from './hooks/other/useConfig';
import { Header } from './layout/Header';
import collateral from './modules/Collateral';
import collateralV2 from './modules/CollateralV2';
import { Credential } from './modules/Credential';
import { Registry } from './modules/Registry';
import { AppSettingsLayout } from './pages/AppSettingsLayout';
import { Login } from './pages/Login';
import { NotFound } from './pages/NotFound';
import { SyncWalletsPage } from './pages/SyncWalletsPage';
import { AllSettings } from './pages/settings/AllSettings';
import { AppRewardConfigurationsPage } from './pages/settings/AppRewardConfigurationsPage';
import { CommercialAgreements } from './pages/settings/CommercialAgreements';
import { Versions } from './pages/settings/versions/Versions';
import { AuthCheck } from './routes/AuthCheck';
import Root from './routes/Root';
import { queryClient } from './utils/QueryClient';

const CommonProviders: React.FC = () => {
  const config = useConfig();
  const { localStorageState } = useLocalStorage();
  const { pollingEnabled } = localStorageState;
  const cnBackendUrl = `${VALIDATOR_API_URL}/v0/scan-proxy`;
  const utilityBackendUrl = config.app.utility_backend_url;
  const qClient = React.useMemo(() => queryClient(pollingEnabled), [pollingEnabled]);
  return (
    <QueryClientProvider client={qClient}>
      <ReactQueryDevtools initialIsOpen={false} />

      <UtilityOperatorProxyApiProvider utilityOperatorUrl={utilityBackendUrl}>
        <CNAllocationApiProvider url={cnBackendUrl}>
          <AllocationApiProvider url={utilityBackendUrl}>
            <JsonApiProvider jsonApiUrl={JSON_API_URL}>
              <ScanProxyApiProvider validatorUrl={VALIDATOR_API_URL}>
                <UtilityAutomationApiClientProvider>
                  <PartiesProvider>
                    <ModuleProvider>
                      <LedgerApiClientProvider>
                        <Outlet />
                      </LedgerApiClientProvider>
                    </ModuleProvider>
                  </PartiesProvider>
                </UtilityAutomationApiClientProvider>
              </ScanProxyApiProvider>
            </JsonApiProvider>
          </AllocationApiProvider>
        </CNAllocationApiProvider>
      </UtilityOperatorProxyApiProvider>
    </QueryClientProvider>
  );
};

const utilityRoutes: RouteObject[] = [
  collateral,
  collateralV2,
  { path: 'credential/*', element: <Credential /> }, // has topbar, has sidebar
  { path: 'registry/*', element: <Registry /> }, // has topbar, has sidebar
  {
    path: 'settings',
    element: <AppSettingsLayout />,
    children: [
      { path: '', element: <AllSettings /> },
      { path: 'commercial-agreements', element: <CommercialAgreements /> },
      { path: 'versions', element: <Versions /> },
      { path: 'app-reward-configs', element: <AppRewardConfigurationsPage /> },
    ],
  },
];

export const App: React.FC = () => {
  const appRoutes = utilityRoutes;
  const sidebarLayout = <Outlet />;

  const indexPage = <Root />;

  const routes: RouteObject = {
    element: <AuthProviders />,
    errorElement: <NotFound />,
    children: [
      { path: 'sync', element: <SyncWalletsPage /> },
      { path: 'login', element: <Login /> },
      {
        element: <AuthCheck />,
        children: [
          {
            element: <CommonProviders />,
            children: [
              {
                element: <Header />,
                children: [
                  { path: '/', element: indexPage, index: true },
                  { element: sidebarLayout, children: appRoutes },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <LocalStorageProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProviderCustom>
          <CssBaseline />
          <RouterProvider
            future={{ v7_startTransition: true }}
            router={createBrowserRouter([routes], {
              future: {
                // Disable this for now because the self-hosted modules use splat paths, which break under the new v7 behavior.
                v7_relativeSplatPath: false,
              },
            })}
          />
        </ThemeProviderCustom>
      </StyledEngineProvider>
    </LocalStorageProvider>
  );
};
